import { useQuery } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fmtDateUk } from "@/lib/utils";
import { DateRangePicker } from "@/components/date-range-picker";
import { Button } from "@/components/ui/button";
import { useDateRange, defaultRange } from "@/lib/use-date-range";

interface ExecSummary {
  sinceDate: string;
  adSpendSince: string;
  opportunities: {
    total: number;
    accepted: number;
    ongoing: number;
    lost: number;
    win_rate_pct: number;
  } | null;
  topProducts: Array<{ product: string; total: number; won: number }>;
  topPeople: Array<{
    creator: string;
    total: number;
    won: number;
    win_rate_pct: number;
  }>;
  adSpend: {
    total_spend: number;
    total_clicks: number;
    total_conversions: number;
  } | null;
  weeklyTrend: Array<{ week: string; opps: number; won: number }>;
}

function fmtInt(n: number | undefined | null): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString();
}

function fmtGbp(n: number | undefined | null): string {
  if (n === null || n === undefined) return "—";
  return `£${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export default function ExecutiveSummary() {
  const { dateRange, setDateRange, from, to, rangeQs } = useDateRange();

  const { data, isLoading, error } = useQuery<ExecSummary>({
    queryKey: ["exec-summary", from, to],
    queryFn: async () => {
      const res = await fetch(`/api/dashboards/executive${rangeQs}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  if (isLoading)
    return (
      <div className="p-6 text-muted-foreground">Loading real KPIs from BigQuery…</div>
    );
  if (error || !data)
    return (
      <div className="p-6 text-red-600">
        Failed to load: {error instanceof Error ? error.message : "unknown error"}
      </div>
    );

  const { opportunities, topProducts, topPeople, adSpend, weeklyTrend, sinceDate, adSpendSince } = data;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Star className="h-6 w-6" /> Executive Summary
          </h1>
          <p className="text-sm text-muted-foreground">
            {from && to ? `${from} → ${to}` : `Opportunities since ${fmtDateUk(sinceDate)} · Ad spend since ${fmtDateUk(adSpendSince)}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="ghost" size="sm" onClick={() => setDateRange(defaultRange())}>Reset</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {opportunities && (
          <>
            <KpiCard label="Opportunities (180d)" value={fmtInt(opportunities.total)} />
            <KpiCard label="Won" value={fmtInt(opportunities.accepted)} />
            <KpiCard label="Win rate" value={`${opportunities.win_rate_pct}%`} />
            <KpiCard label="In flight" value={fmtInt(opportunities.ongoing)} />
          </>
        )}
        {adSpend && (
          <>
            <KpiCard label="Ad spend (30d)" value={fmtGbp(adSpend.total_spend)} />
            <KpiCard label="Ad clicks (30d)" value={fmtInt(adSpend.total_clicks)} />
            <KpiCard label="Ad conversions (30d)" value={fmtInt(adSpend.total_conversions)} />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Top products (by wins)</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1">Product</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Won</th>
                </tr>
              </thead>
              <tbody>
                {topProducts.map((p) => (
                  <tr key={p.product} className="border-t">
                    <td className="py-1.5 pr-2">{p.product}</td>
                    <td className="text-right">{fmtInt(p.total)}</td>
                    <td className="text-right font-medium">{fmtInt(p.won)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top people (by wins)</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1">Creator</th>
                  <th className="text-right">Opps</th>
                  <th className="text-right">Won</th>
                  <th className="text-right">Rate</th>
                </tr>
              </thead>
              <tbody>
                {topPeople.map((p) => (
                  <tr key={p.creator} className="border-t">
                    <td className="py-1.5 pr-2">{p.creator}</td>
                    <td className="text-right">{fmtInt(p.total)}</td>
                    <td className="text-right">{fmtInt(p.won)}</td>
                    <td className="text-right font-medium">{p.win_rate_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Weekly opportunity trend</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1">Week</th>
                <th className="text-right">Opportunities created</th>
                <th className="text-right">Won</th>
              </tr>
            </thead>
            <tbody>
              {weeklyTrend.map((w) => (
                <tr key={w.week} className="border-t">
                  <td className="py-1.5 pr-2">{w.week}</td>
                  <td className="text-right">{fmtInt(w.opps)}</td>
                  <td className="text-right font-medium">{fmtInt(w.won)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-muted-foreground text-xs">{label}</div>
        <div className="text-xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}
